/**
 * The AffDice SuperAdmin intro tour: its steps, in order, and the version they make up.
 *
 * Raise `AMS_TOUR_VERSION` when a step is added for a new module, and give
 * that step `since` set to the new version: the rail's tour entry is then
 * highlighted for everybody who saw an older tour, and opening it starts on
 * the new step. The tour never starts by itself again.
 *
 * Plans is left out on purpose: its screen is not built yet, and a tour that
 * sends somebody to an empty page is worse than one that says nothing.
 *
 * Kept free of React so a test can read it without a DOM.
 */
import type { TourStep } from '@affdice/portal-kit';

import { t, type AmsMessageKey } from './messages.ts';
import type { RouteId } from './routes.ts';

export const AMS_TOUR_VERSION = 1;

interface AmsTourStep {
  readonly id: string;
  /** Absent: the step's title is the screen's own menu label. */
  readonly titleKey?: AmsMessageKey;
  readonly bodyKey: AmsMessageKey;
  /** The screen the step describes, and the menu label that names it. */
  readonly route?: RouteId;
  readonly navKey?: AmsMessageKey;
  readonly since?: number;
}

export const AMS_TOUR: readonly AmsTourStep[] = [
  { id: 'welcome', titleKey: 'intro.welcome.title', bodyKey: 'intro.welcome.body' },
  { id: 'overview', bodyKey: 'intro.overview.body', route: 'overview', navKey: 'nav.overview' },
  { id: 'operators', bodyKey: 'intro.operators.body', route: 'operators', navKey: 'nav.operators' },
  { id: 'brands', bodyKey: 'intro.brands.body', route: 'brands', navKey: 'nav.brands' },
  { id: 'affiliates', bodyKey: 'intro.affiliates.body', route: 'affiliates', navKey: 'nav.affiliates' },
  { id: 'team', titleKey: 'intro.team.title', bodyKey: 'intro.team.body', route: 'users', navKey: 'nav.users' },
  {
    id: 'setup', titleKey: 'intro.setup.title', bodyKey: 'intro.setup.body',
    route: 'reference-data', navKey: 'nav.referenceData',
  },
  {
    id: 'integrity', titleKey: 'intro.integrity.title', bodyKey: 'intro.integrity.body',
    route: 'consumers', navKey: 'nav.consumers',
  },
  {
    id: 'access', titleKey: 'intro.access.title', bodyKey: 'intro.access.body',
    route: 'access-log', navKey: 'nav.accessLog',
  },
  { id: 'done', titleKey: 'intro.done.title', bodyKey: 'intro.done.body' },
];

/**
 * The steps in this tab's language. Every AffDice SuperAdmin screen opens for every
 * member of platform staff (a screen narrows what each may *do* inside it),
 * so no step is dropped for a role here.
 */
export function amsTourSteps(go: (route: RouteId) => void): TourStep[] {
  return AMS_TOUR.map((s) => {
    const screen = s.navKey ? t(s.navKey) : undefined;
    return {
      id: s.id,
      title: s.titleKey ? t(s.titleKey) : screen ?? '',
      body: t(s.bodyKey),
      ...(s.since !== undefined ? { since: s.since } : {}),
      ...(s.route && screen
        ? { go: { label: t('intro.go', { screen }), onGo: () => go(s.route!) } }
        : {}),
    };
  });
}
